import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import DataTable from '../../components/ui/data-table';
import EmptyState from '../../components/ui/empty-state.jsx';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { useAssignments } from '../../hooks/useAssignments';
import { FileText, Filter, Calendar, Clock, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';

const StudentAssignments = () => {
  const { assignments, loading, error, fetchAssignments } = useAssignments({ scope: 'student' });

  const columns = [
    { 
      id: 'title', 
      header: 'Assignment', 
      accessorKey: 'title', 
      sortable: true,
      cell: ({ row }) => (
        <div className="flex items-center space-x-2">
          <FileText className="h-4 w-4 text-blue-600" />
          <div>
            <div className="font-medium">{row.title || row.assignment?.title}</div>
            <div className="text-sm text-gray-500">{(row.description || row.assignment?.description || '').substring(0, 50)}...</div>
          </div>
        </div>
      )
    },
    { 
      id: 'subject', 
      header: 'Subject', 
      accessorKey: 'subjectName', 
      sortable: true,
      cell: ({ row }) => {
        const subjectName = row.subjectName || 
                           row.assignment?.subject?.name || 
                           (typeof row.subject === 'object' && row.subject?.name) || 
                           (typeof row.subject === 'string' ? row.subject : null) ||
                           'N/A';
        return <Badge variant="outline">{subjectName}</Badge>;
      }
    },
    { 
      id: 'class', 
      header: 'Class', 
      accessorKey: 'className',
      cell: ({ row }) => {
        const className = row.className || 
                         row.assignment?.class?.name || 
                         (typeof row.class === 'object' && row.class?.name) || 
                         (typeof row.class === 'string' ? row.class : null) ||
                         'N/A';
        return <span className="text-sm">{className}</span>;
      }
    },
    { 
      id: 'dueDate', 
      header: 'Due Date', 
      accessorKey: 'dueDate', 
      sortable: true,
      cell: ({ row }) => {
        const dueDateStr = row.dueDate || row.assignment?.dueDate;
        if (!dueDateStr) return <span className="text-gray-400">N/A</span>;
        const dueDate = new Date(dueDateStr);
        const status = row.status || row.assignment?.status;
        const isOverdue = dueDate < new Date() && status !== 'submitted' && status !== 'graded';
        return (
          <div className="flex items-center space-x-1">
            <Calendar className="h-4 w-4 text-gray-500" />
            <span className={isOverdue ? 'text-red-600 font-medium' : ''}>
              {dueDate.toLocaleDateString()}
            </span>
            {isOverdue && <AlertCircle className="h-4 w-4 text-red-500" />}
          </div>
        );
      }
    },
    { 
      id: 'status', 
      header: 'Status', 
      accessorKey: 'status', 
      sortable: true,
      cell: ({ row }) => {
        const status = row.status || row.assignment?.status || 'pending';
        const variants = {
          'pending': { variant: 'secondary', icon: Clock },
          'submitted': { variant: 'default', icon: CheckCircle },
          'graded': { variant: 'outline', icon: CheckCircle },
          'overdue': { variant: 'destructive', icon: AlertCircle }
        };
        const config = variants[status] || variants['pending'];
        const Icon = config.icon;
        return (
          <Badge variant={config.variant} className="flex items-center space-x-1">
            <Icon className="h-3 w-3" />
            <span className="capitalize">{status}</span>
          </Badge>
        );
      }
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const assignmentId = row.id || row.assignmentId || row.assignment?.id;
        const status = row.status || row.assignment?.status;
        return (
          <div className="flex items-center gap-2">
            <Button 
              size="sm" 
              onClick={() => window.location.assign(`/dashboard/assignments/${assignmentId}`)}
            >
              View
            </Button>
            {status !== 'submitted' && status !== 'graded' && (
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => window.location.assign(`/dashboard/assignments/${assignmentId}?submit=true`)}
              >
                Submit
              </Button>
            )}
          </div>
        );
      }
    }
  ];

  if (loading) return <LoadingSpinner />;
  if (error) return (
    <div className="p-6">
      <Card>
        <CardContent className="p-6">
          <div className="text-center">
            <div className="text-red-600 text-sm mb-4">{String(error)}</div>
            <Button onClick={() => fetchAssignments()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">My Assignments</h2>
          <p className="text-gray-600 mt-1">View and submit your assignments</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => fetchAssignments({ status: 'pending' })}>
            <Filter className="w-4 h-4 mr-2" /> Pending
          </Button>
          <Button variant="outline" size="sm" onClick={() => fetchAssignments({ status: 'submitted' })}>
            <Filter className="w-4 h-4 mr-2" /> Submitted
          </Button>
          <Button variant="outline" size="sm" onClick={() => fetchAssignments({})}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Reset
          </Button>
        </div>
      </div>

      {(!assignments || assignments.length === 0) ? (
        <EmptyState 
          icon={FileText}
          title="No assignments found"
          description="You have no assignments at the moment. Check back later or contact your teacher if you believe this is an error."
          action
          actionLabel="Refresh"
          onAction={() => fetchAssignments()}
        />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <FileText className="h-5 w-5" />
              <span>Assignments ({assignments.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <DataTable 
              data={assignments} 
              columns={columns} 
              searchable 
              searchPlaceholder="Search assignments..."
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StudentAssignments;


